
// Moved from reportFormatting.ts
export const cleanupText = (text: string): string => {
  if (!text) return "";

  // Check if this is an error message or an upgrade prompt message
  if (
    text.toLowerCase().includes("error") ||
    text.toLowerCase().includes("failed") ||
    text.toLowerCase().includes("limit") ||
    text.toLowerCase().includes("exceeded") ||
    text.toLowerCase().includes("i'm sorry") ||
    text.toLowerCase().includes("couldn't generate") ||
    text.toLowerCase().includes("you've reached") ||
    text.toLowerCase().includes("please subscribe") ||
    text.toLowerCase().includes("thank you for using your free report") ||
    text.toLowerCase().includes("apologize") ||
    text.toLowerCase().includes("try again") ||
    text.toLowerCase().includes("unavailable")
  ) {
    // For error messages, remove any markdown formatting to prevent bold text
    return text
      .replace(/\*\*/g, "") // Remove bold formatting
      .replace(/\*/g, "") // Remove italic formatting
      .replace(/__/g, "") // Remove underline formatting
      .replace(/_/g, "") // Remove emphasis
      .replace(/##/g, "") // Remove headings
      .replace(/#/g, "") // Remove any remaining hashtags
      .replace(/<strong>([^<]+)<\/strong>/g, "$1") // Remove HTML strong tags
      .replace(/<b>([^<]+)<\/b>/g, "$1") // Remove HTML b tags
      .replace(/<em>([^<]+)<\/em>/g, "$1") // Remove HTML em tags
      .replace(/<i>([^<]+)<\/i>/g, "$1"); // Remove HTML i tags
  }

  // Special handling for Introduction & Methodology - ensure it's NEVER bold
  if (text.toLowerCase().includes("introduction") && text.toLowerCase().includes("methodology")) {
    return text
      .replace(/\*\*(.*?introduction.*?methodology.*?)\*\*/gi, "$1")
      .replace(/\*(.*?introduction.*?methodology.*?)\*/gi, "$1")
      .replace(/<strong>(.*?introduction.*?methodology.*?)<\/strong>/gi, "$1")
      .replace(/<b>(.*?introduction.*?methodology.*?)<\/b>/gi, "$1")
      .replace(/<em>(.*?introduction.*?methodology.*?)<\/em>/gi, "$1")
      .replace(/<i>(.*?introduction.*?methodology.*?)<\/i>/gi, "$1")
      // Then apply normal formatting removal
      .replace(/\*\*([^*]+)\*\*/g, "$1") // Remove bold markdown
      .replace(/\*([^*]+)\*/g, "$1") // Remove italic markdown
      .replace(/<strong>([^<]+)<\/strong>/g, "$1") // Remove HTML strong tags
      .replace(/<b>([^<]+)<\/b>/g, "$1") // Remove HTML b tags
      .replace(/<em>([^<]+)<\/em>/g, "$1") // Remove HTML em tags
      .replace(/<i>([^<]+)<\/i>/g, "$1") // Remove HTML i tags
      .replace(/^#+\s*/gm, "") // Remove markdown headings completely
      .replace(/(?<!#)#(?!#)/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/^[-•*]\s*/gm, "") // Remove bullet points at start of lines
      .replace(/^--+\s*/gm, "") // Remove multiple dashes at start of lines
      .replace(/^---+$/gm, "") // Remove standalone dash lines completely
      .replace(/\n---+\n/g, "\n") // Remove lines that just contain dashes with newlines
      .replace(/\n--+\n/g, "\n") // Remove shorter dash-only lines too
      .replace(/^--+$/gm, "") // Remove standalone dash lines completely (again for safety)
      .trim();
  }

  // For ALL sections, remove all bolding and formatting 
  // This ensures uniform text appearance across the entire report
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1") // Remove bold markdown
    .replace(/\*([^*]+)\*/g, "$1") // Remove italic markdown
    .replace(/<strong>([^<]+)<\/strong>/g, "$1") // Remove HTML strong tags
    .replace(/<b>([^<]+)<\/b>/g, "$1") // Remove HTML b tags
    .replace(/<em>([^<]+)<\/em>/g, "$1") // Remove HTML em tags
    .replace(/<i>([^<]+)<\/i>/g, "$1") // Remove HTML i tags
    .replace(/^#+\s*/gm, "") // Remove markdown headings completely
    .replace(/(?<!#)#(?!#)/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^[-•*]\s*/gm, "") // Remove bullet points at start of lines
    .replace(/^--+\s*/gm, "") // Remove multiple dashes at start of lines
    .replace(/^---+$/gm, "") // Remove standalone dash lines completely
    .replace(/\n---+\n/g, "\n") // Remove lines that just contain dashes with newlines
    .replace(/\n--+\n/g, "\n") // Remove shorter dash-only lines too
    .replace(/^--+$/gm, "") // Remove standalone dash lines completely (again for safety)
    .trim();
};

export const parseTableRows = (
  rows: string[]
): { cells: string[]; isHeader: boolean; isDelimiter: boolean }[] => {
  return rows.map((row, index) => ({
    cells: row.split("|").filter((cell) => cell.trim()),
    isHeader: index === 0,
    isDelimiter: row.includes("-|-") || row.includes("|-|"),
  }));
};

export const formatBulletPoints = (items: string[]): string[] => {
  return items
    .filter((item) => item.trim())
    .map((item) => {
      // Remove bullet points but preserve citations
      return item
        .replace(/^[-•*]\s*/, "")
        .replace(/\[(\d+)\]/g, "<sup>[$1]</sup>");
    });
};

export const formatNumberedList = (items: string[]): string[] => {
  return items.filter(Boolean).map((item) => {
    // Remove numbering but preserve citations
    return item
      .replace(/^\d+\.\s*/, "")
      .replace(/\[(\d+)\]/g, "<sup>[$1]</sup>");
  });
};

/**
 * Formats text for consistent display across the application.
 * Removes ALL formatting markers, standardizes citations, and cleans up text.
 */
export const formatText = (text: string): string => {
  if (!text) return "";

  // Special handling for Introduction & Methodology sections - ensure never bold
  if (text.toLowerCase().includes("introduction") && text.toLowerCase().includes("methodology")) {
    let processedText = text
      .replace(/\*\*(.*?introduction.*?methodology.*?)\*\*/gi, "$1")
      .replace(/\*(.*?introduction.*?methodology.*?)\*/gi, "$1")
      .replace(/<strong>(.*?introduction.*?methodology.*?)<\/strong>/gi, "$1")
      .replace(/<b>(.*?introduction.*?methodology.*?)<\/b>/gi, "$1")
      // Then apply normal formatting removal for the rest of the text
      .replace(/\*\*([^*]+)\*\*/g, "$1") // Remove bold markdown
      .replace(/\*([^*]+)\*/g, "$1") // Remove italic markdown
      .replace(/<strong>([^<]+)<\/strong>/g, "$1") // Remove HTML strong tags
      .replace(/<b>([^<]+)<\/b>/g, "$1") // Remove HTML b tags
      .replace(/<em>([^<]+)<\/em>/g, "$1") // Remove HTML em tags
      .replace(/<i>([^<]+)<\/i>/g, "$1"); // Remove HTML i tags

    // Handle citations
    processedText = processedText.replace(
      /\[(\d+)\]/g,
      '<sup><a href="#reference-$1" class="text-blue-400 hover:text-blue-300">[$1]</a></sup>'
    );

    return processedText
      .replace(/^#+\s*/gm, "") // Remove heading markers
      .replace(/^[-•*]\s*/gm, "") // Remove bullet points
      .replace(/\[([^\]]+)\]/g, "$1") // Remove brackets around placeholder text
      .replace(/(\d+)\s+percent/gi, "$1%") // Convert "XX percent" to "XX%"
      .replace(/\\([^\\])/g, "$1") // Remove stray backslashes
      .replace(/\s{2,}/g, " ") // Clean up any double spaces
      .replace(/\n{3,}/g, "\n\n"); // Normalize paragraph spacing
  }

  // Remove ALL bold and italic formatting - applied to ALL text regardless of section type
  let processedText = text
    .replace(/\*\*([^*]+)\*\*/g, "$1") // Remove bold markdown
    .replace(/\*([^*]+)\*/g, "$1") // Remove italic markdown
    .replace(/<strong>([^<]+)<\/strong>/g, "$1") // Remove HTML strong tags
    .replace(/<b>([^<]+)<\/b>/g, "$1") // Remove HTML b tags
    .replace(/<em>([^<]+)<\/em>/g, "$1") // Remove HTML em tags
    .replace(/<i>([^<]+)<\/i>/g, "$1"); // Remove HTML i tags

  // Handle citations - make them consistent and clickable in UI
  processedText = processedText.replace(
    /\[(\d+)\]/g,
    '<sup><a href="#reference-$1" class="text-blue-400 hover:text-blue-300">[$1]</a></sup>'
  );

  // Identify and format financial performance, industry trends, and similar sections
  if (processedText.match(/^\d+\.\s+(Financial Performance|Key Industry Trends|Market Overview|Competitive Analysis)/i)) {
    return processedText;
  }

  // Remove any remaining markdown formatting characters including hashtags
  return (
    processedText
      .replace(/^#+\s*/gm, "") // Remove heading markers
      // Remove bullet points and dashes at start of paragraphs
      .replace(/^[-•*]\s*/gm, "")
      .replace(/^--+\s*/gm, "")
      // Remove brackets around placeholder text
      .replace(/\[([^\]]+)\]/g, "$1")
      // Convert "XX percent" to "XX%"
      .replace(/(\d+)\s+percent/gi, "$1%")
      // Remove stray backslashes
      .replace(/\\([^\\])/g, "$1")
      // Clean up any double spaces
      .replace(/\s{2,}/g, " ")
      // Normalize paragraph spacing
      .replace(/\n{3,}/g, "\n\n")
  );
};

/**
 * Formats text specifically for PDF export.
 * Removes HTML tags and special characters for clean PDF output.
 */
export const formatTextForPdf = (text: string): string => {
  if (!text) return "";

  // Remove HTML tags
  let formattedText = text.replace(/<\/?[^>]+(>|$)/g, "");

  // Convert entities like &quot; to their actual characters
  formattedText = formattedText
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

  // Remove ALL markdown formatting symbols (*, #)
  formattedText = formattedText.replace(/[*#]/g, "");

  // Keep citation references for PDF but in a simplified format
  formattedText = formattedText.replace(/\[(\d+)\]/g, "[$1]");

  // Remove bullet points and dashes at start of lines
  formattedText = formattedText.replace(/^[-•*]\s*/gm, "");
  formattedText = formattedText.replace(/^--+\s*/gm, "");

  // Remove stray backslashes
  formattedText = formattedText.replace(/\\([^\\])/g, "$1");

  // Remove brackets around placeholder text
  formattedText = formattedText.replace(/\[([^\]]+)\]/g, "$1");

  // Convert "XX percent" to "XX%"
  formattedText = formattedText.replace(/(\d+)\s+percent/gi, "$1%");

  // Clean up extra whitespace but preserve paragraph breaks
  formattedText = formattedText.replace(/\n{3,}/g, "\n\n"); // Max 2 newlines
  formattedText = formattedText.replace(/\s{2,}/g, " "); // Collapse multiple spaces

  return formattedText.trim();
};

export const formatCitation = (text: string): string => {
  if (!text) return "";

  // Replace citation references with superscript format
  return text.replace(/\[(\d+)\]/g, "<sup>[$1]</sup>");
};

export const cleanForPdf = (text: string): string => {
  if (!text) return "";

  return (
    text
      // First remove HTML tags
      .replace(/<\/?[^>]+(>|$)/g, "")
      // Remove markdown formatting - ensure all * and # are removed
      .replace(/[*#]/g, "")
      // Remove bullet points and dashes at start
      .replace(/^[-•*]\s*/gm, "")
      .replace(/^--+\s*/gm, "")
      // Remove stray backslashes
      .replace(/\\([^\\])/g, "$1")
      // Clean up any double spaces
      .replace(/\s{2,}/g, " ")
      .trim()
  );
};

export const cleanTableForPdf = (text: string): string => {
  return cleanForPdf(text);
};

export const extractSections = (
  content: string
): { title: string; content: string }[] => {
  // Split content into sections based on markdown headings
  const sectionRegex = /^#+\s+(.*?)(?=\n#+\s+|\n*$)/gms;
  const matches = [...content.matchAll(sectionRegex)];

  if (matches.length === 0) {
    // If no sections found, use the entire content
    return [{ title: "Content", content }];
  }

  const sections = [];
  for (let i = 0; i < matches.length; i++) {
    const current = matches[i];
    const next = matches[i + 1];

    // Get the section title from the current match
    const title = current[1].trim();

    // Calculate the content between this header and the next
    const startIndex = current.index! + current[0].length;
    const endIndex = next ? next.index! : content.length;
    const sectionContent = content.slice(startIndex, endIndex).trim();

    sections.push({ title, content: sectionContent });
  }

  return sections;
};

export const extractReferences = (content: string): string => {
  const referencesMatch = content.match(/##?\s*References\s*\n([\s\S]*)/i);
  return referencesMatch ? referencesMatch[1].trim() : "";
};

export const formatReferencesForPdf = (referencesText: string): string => {
  if (!referencesText) return "";

  // First, try to split by numbered references with period (1., 2., etc.)
  let references = referencesText
    .split(/\n\s*\d+\.\s+/)
    .filter((ref) => ref.trim().length > 0);

  // If no matches, try splitting by bracketed references ([1], [2], etc.)
  if (references.length <= 1) {
    references = referencesText
      .split(/\n\s*\[\d+\]\s+/)
      .filter((ref) => ref.trim().length > 0);
  }

  // If still no matches, try splitting by newlines
  if (references.length <= 1) {
    references = referencesText
      .split(/\n{2,}/)
      .filter((ref) => ref.trim().length > 0);
  }

  // Format each reference with proper numbering and cleaning
  return references
    .map((ref, index) => `${index + 1}. ${cleanForPdf(ref)}`)
    .join("\n\n");
};

/**
 * Cleans markdown syntax from the provided text
 * Removes heading markers, emphasis markers, etc.
 */
export const cleanMarkdownSyntax = (text: string): string => {
  if (!text) return '';
  
  return text
    // Remove heading markers (# ## ###)
    .replace(/^#+\s+/gm, '')
    // Remove bold markers
    .replace(/\*\*/g, '')
    // Remove italic markers
    .replace(/\*/g, '')
    // Remove underline markers
    .replace(/\_\_/g, '')
    // Remove emphasis
    .replace(/\_/g, '')
    // Remove any standalone # that isn't part of a heading
    .replace(/(?<![#])[#](?![# ])/g, '')
    .trim();
};
